import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import CustomModal from '../CustomModal'
import ModalButton from './ModalButton'
import { selectIsNeededZIndex } from '../../../../redux/selector/system'
import KeyBindings from '../../keybindings'
import { ButtonsModalSectionColumn, ButtonsModalSectionsGrid, ButtonsModalWrapper, ModalButtonContent, ModalButtonSkeleton } from './index.styled'

const ButtonsModal = React.memo(({
	isOpen = false, close, title, titlePrice, size, id, enableOutsideClick,
	zIndex = 1, returnZIndex = 0, autoFocus = true, buttons = [], children,
	mobileIndentIsNeeded = false, className, isLoading = false, sections = null,
}) => {
	const isNeededZIndex = useSelector(selectIsNeededZIndex(zIndex))
	const [activeButtonIndex, setActiveButtonIndex] = useState(0)
	const [onClickIsProcessing, setOnClickIsProcessing] = useState(false)
	const visibleButtons = useMemo(() => buttons.filter(btn => btn.show), [buttons])
	const isButtonsDisabled = !isOpen || onClickIsProcessing
	const layout = (mobileIndentIsNeeded ? 'mobile-indent ' : '') + className

	const focusFirstChild = useCallback(() => {
		if (autoFocus) {
			setActiveButtonIndex(0)
		}
	}, [autoFocus])

	useEffect(() => {
		if (isOpen) {
			focusFirstChild()
		}
	}, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleArrowUp = useCallback(() => {
		if (!visibleButtons[activeButtonIndex - 1]) {
			setActiveButtonIndex(visibleButtons.length - 1)
		} else {
			setActiveButtonIndex(activeButtonIndex - 1)
		}
	}, [visibleButtons, activeButtonIndex])

	const handleArrowDown = useCallback(() => {
		if (!visibleButtons[activeButtonIndex + 1]) {
			setActiveButtonIndex(0)
		} else {
			setActiveButtonIndex(activeButtonIndex + 1)
		}
	}, [visibleButtons, activeButtonIndex])

	const handleProcessClick = useCallback(async (button, event = null) => {
		setOnClickIsProcessing(true)
		await (event ? button.onClick(event) : button.onClick())
		setOnClickIsProcessing(false)
	}, [])

	const handleButtonClick = useCallback(async (e, button, index) => {
		setActiveButtonIndex(index)
		await handleProcessClick(button, e)
	}, [handleProcessClick])

	const handleEnter = useCallback(async () => {
		const selectedButton = visibleButtons[activeButtonIndex]
		const isDisabled = selectedButton.disabled || isButtonsDisabled
		if (isDisabled) return
		await handleProcessClick(selectedButton)
	}, [visibleButtons, activeButtonIndex, handleProcessClick, isButtonsDisabled])

	return (
		<>
			<CustomModal {...{
				isOpen, close, title, titlePrice, size, id,
				enableOutsideClick, zIndex, returnZIndex,
			}}>
				<ButtonsModalWrapper className={layout}>
					{children}
					{isLoading ?
						Array.from({ length: 4 }, (_, i) =>
							<ModalButtonContent key={i}>
								<ModalButtonSkeleton as="div" data-testid="skeleton-button" />
							</ModalButtonContent>) :
						sections ?
							<ButtonsModalSectionsGrid>
								{sections.map(section =>
									<ButtonsModalSectionColumn key={section.key} data-testid={section.testId}>
										{section.title}
										{visibleButtons.map((button, index) => button.section === section.key &&
											(button.customButton ?
												button.customButton({
													key: index,
													index,
													isActive: index === activeButtonIndex,
													isDisabled: button.disabled || isButtonsDisabled,
													onClick: e => handleButtonClick(e, button, index),
												}) :
												<ModalButton key={index}
																		 index={index}
																		 tabIndex={index}
																		 button={button}
																		 onClick={handleButtonClick}
																		 isDisabled={button.disabled || isButtonsDisabled}
																		 isActive={index === activeButtonIndex} />))}
									</ButtonsModalSectionColumn>)}
							</ButtonsModalSectionsGrid> :
							visibleButtons?.map((button, index) =>
								button.customButton ?
									button.customButton({
										key: index,
										index,
										isActive: index === activeButtonIndex,
										isDisabled: button.disabled || isButtonsDisabled,
										onClick: e => handleButtonClick(e, button, index),
									}) :
									<ModalButton key={index}
															 index={index}
															 tabIndex={index}
															 button={button}
															 onClick={handleButtonClick}
															 isDisabled={button.disabled || isButtonsDisabled}
															 isActive={index === activeButtonIndex} />)}
				</ButtonsModalWrapper>
			</CustomModal>

			{isNeededZIndex &&
				<KeyBindings enter={handleEnter}
										 arrowUp={handleArrowUp}
										 arrowDown={handleArrowDown} />}
		</>
	)
})

ButtonsModal.displayName = 'ButtonsModal'
export default ButtonsModal
